// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Hotel, Ticket, Video, Users, FileText, Camera, Printer } from 'lucide-react';
import SpectatorTicketsBox from './SpectatorTicketsBox';
import MediaCredentialsModal from './MediaCredentialsModal';
import CoachAttendanceModal from './CoachAttendanceModal';
import NcaaLiveCoachModal from './NcaaLiveCoachModal';
import { supabase } from '@/integrations/supabase/client';

interface Coach {
  id: string;
  coach_name: string;
  college_id: string | null;
  college_name: string | null;
  division: string;
  logo_url: string | null;
  external_url: string | null;
}

interface EventData {
  id: string;
  name?: string;
  hotel_booking_url?: string;
  live_streaming_url?: string;
  coaches_external_url?: string;
  rules?: string;
  media_credentials_description?: string;
  media_credentials_form_id?: string;
  media_credentials_notification_email?: string;
  ticket_display_text?: string;
  more_info?: string;
  coach_attendance_fee?: number;
  media_credentials_price?: number;
  is_ncaa_live_event?: boolean;
  ncaa_d1_first_coach_fee?: number;
  ncaa_d1_additional_coach_fee?: number;
  ncaa_other_first_coach_fee?: number;
  ncaa_other_additional_coach_fee?: number;
  ncaa_hospitality_days?: string[];
  coach_coupon_code?: string;
  ncaa_coupon_code?: string;
  ncaa_coupon_discount_type?: 'percentage' | 'fixed';
  ncaa_coupon_discount_value?: number;
  coach_registration_instructions?: string;
}

interface EventHelpfulInformationProps {
  event?: EventData;
}

const EventHelpfulInformation: React.FC<EventHelpfulInformationProps> = ({ event }) => {
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [isMediaCredentialsModalOpen, setIsMediaCredentialsModalOpen] = useState(false);
  const [showCoachesModal, setShowCoachesModal] = useState(false);
  const [attendingCoaches, setAttendingCoaches] = useState<Coach[]>([]);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  
  // Fetch attending coaches
  useEffect(() => {
    if (event?.id) {
      fetchAttendingCoaches();
    }
  }, [event?.id]);

  const fetchAttendingCoaches = async () => {
    if (!event?.id) return;
    
    try {
      setLoadingCoaches(true);
      const { data, error } = await supabase
        .from('event_coaches')
        .select('*')
        .eq('event_id', event.id)
        .neq('hide_from_public', true)
        .in('payment_status', ['paid', 'comp'])
        .order('college_name');

      if (error) throw error;

      const processedData = data?.map((coach: any) => ({
        id: coach.id,
        coach_name: coach.coach_name,
        college_id: coach.college_id,
        college_name: coach.college_name,
        division: coach.division,
        logo_url: coach.logo_url,
        external_url: coach.external_url
      })) || [];

      setAttendingCoaches(processedData);
    } catch (error) {
      console.error('Error fetching attending coaches:', error);
    } finally {
      setLoadingCoaches(false);
    }
  };

  const handleHotelBooking = () => {
    if (event?.hotel_booking_url) {
      window.open(event.hotel_booking_url, '_blank');
    }
  };

  const handleLiveStream = () => {
    if (event?.live_streaming_url) {
      window.open(event.live_streaming_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleGameRulesClick = () => {
    if (event?.rules?.trim()) {
      setIsRulesModalOpen(true);
    }
  };

  const handleMediaCredentialsClick = () => {
    setIsMediaCredentialsModalOpen(true);
  };

  const handlePrintRules = () => {
    const printContent = document.getElementById('game-rules-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Game Rules</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #002E62; }
              </style>
            </head>
            <body>
              <h1>Game Rules</h1>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const isLiveStreamAvailable = event?.live_streaming_url?.trim();
  const hasGameRules = event?.rules?.trim();
  const hasMediaCredentials = event?.media_credentials_description?.trim() || event?.media_credentials_form_id?.trim();
  
  return (
    <div className="mb-8">
      <h3 className="text-xl font-semibold mb-4">Helpful Information</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Spectator Tickets box - conditionally renders based on active tickets */}
        {event?.id && (
          <SpectatorTicketsBox eventId={event.id} ticketDisplayText={event.ticket_display_text} />
        )}

        {/* Hotels card - only show if hotel_booking_url exists and is not empty */}
        {event?.hotel_booking_url?.trim() && (
          <Card className="flex flex-col">
            <CardHeader className="flex-1">
              <Hotel className="w-8 h-8 mx-auto mb-2 text-primary" />
              <h3 className="font-medium text-sm">Hotels</h3>
              <p className="text-xs text-gray-600 mt-1">Find nearby accommodations</p>
              <p className="text-xs text-muted-foreground mt-2">
                This event is a Stay-and-Play event, requiring all teams and their guests in need of hotels to reserve their rooms through our hotel provider.
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <Button 
                variant="default" 
                onClick={handleHotelBooking}
                className="w-full"
              >
                Book Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Live Stream card - always show but conditionally enable */}
        <Card 
          className={`flex flex-col ${
            isLiveStreamAvailable 
              ? '' 
              : 'opacity-50'
          }`}
        >
          <CardHeader className="flex-1">
            <Video className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-sm">Live Stream</h4>
            <p className="text-xs text-gray-600 mt-1 flex-1">
              {isLiveStreamAvailable ? 'Watch live coverage' : 'Live coverage unavailable'}
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            <Button 
              variant="default" 
              onClick={handleLiveStream}
              disabled={!isLiveStreamAvailable}
              className="w-full"
            >
              Watch Now
            </Button>
          </CardContent>
        </Card>
        
        {/* Coaches card */}
        <Card className="flex flex-col">
          <CardHeader className="flex-1">
            <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-sm">College Coaches</h4>
            <p className="text-xs text-gray-600 mt-1 flex-1">Are you a college coach who will be attending? Let us know</p>
          </CardHeader>
          <CardContent className="pt-0">
            <Button 
              variant="default" 
              onClick={() => {
                if (event?.coaches_external_url) {
                  window.open(event.coaches_external_url, '_blank', 'noopener,noreferrer');
                } else {
                  setShowCoachesModal(true);
                }
              }}
              className="w-full"
            >
              I will be attending
            </Button>
          </CardContent>
        </Card>

        {/* Game Rules card - updated layout with button */}
        <Card 
          className={`flex flex-col ${
            hasGameRules 
              ? '' 
              : 'opacity-50'
          }`}
        >
          <CardHeader className="flex-1">
            <FileText className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-sm">Game Rules</h4>
            <p className="text-xs text-gray-600 mt-1 flex-1">
              {hasGameRules ? 'View the rules for this event' : 'Rules not available'}
            </p>
          </CardHeader>
          {hasGameRules && (
            <CardContent className="pt-0">
              <Button 
                variant="default" 
                onClick={handleGameRulesClick}
                className="w-full"
              >
                View Rules
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Media Credentials card */}
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow flex flex-col"
          onClick={handleMediaCredentialsClick}
        >
          <CardHeader className="flex-1">
            <Camera className="w-8 h-8 mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-sm">Media Credentials</h4>
            <p className="text-xs text-gray-600 mt-1 flex-1">
              {event?.media_credentials_description 
                ? event.media_credentials_description.replace(/<[^>]*>/g, '').substring(0, 100) + (event.media_credentials_description.replace(/<[^>]*>/g, '').length > 100 ? '...' : '')
                : 'Press accreditation'
              }
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            <Button 
              variant="default" 
              onClick={handleMediaCredentialsClick}
              className="w-full"
            >
              Request Creds
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Attending Coaches Section */}
      {attendingCoaches.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4">Coaches Attending This Event</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {attendingCoaches.map((coach) => (
              <Card 
                key={coach.id} 
                className={`overflow-hidden ${coach.external_url ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
                onClick={() => {
                  if (coach.external_url) {
                    window.open(coach.external_url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <CardContent className="p-3 text-center">
                  <div className="w-16 h-16 mx-auto mb-2 bg-gray-100 rounded flex items-center justify-center">
                    {coach.logo_url ? (
                      <img
                        src={coach.logo_url}
                        alt={`${coach.college_name || 'College'} logo`}
                        className="w-full h-full object-contain rounded"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/20 rounded flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">
                          {coach.college_name?.substring(0, 2).toUpperCase() || 'C'}
                        </span>
                      </div>
                    )}
                  </div>
                  <h4 className="font-medium text-sm mb-1">
                    {coach.college_name || 'College Name'}
                  </h4>
                  <Badge variant="secondary" className="text-xs">
                    {coach.division}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* More Info Section - only show if there's actual content */}
      {event?.more_info && event.more_info.replace(/<[^>]*>/g, '').trim() && (
        <div className="mt-8">
          <div 
            className="bg-primary/10 border-2 border-primary/20 rounded-lg p-6 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
            dangerouslySetInnerHTML={{ __html: event.more_info }}
          />
        </div>
      )}

      {/* Game Rules Modal */}
      <Dialog open={isRulesModalOpen} onOpenChange={setIsRulesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Game Rules</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrintRules}
              className="ml-auto"
            >
              <Printer className="w-4 h-4" />
            </Button>
          </DialogHeader>
          <div 
            id="game-rules-content"
            className="mt-4 prose prose-blue max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
            dangerouslySetInnerHTML={{ __html: event?.rules || '' }}
          />
        </DialogContent>
      </Dialog>

      {/* Media Credentials Modal */}
      <MediaCredentialsModal
        isOpen={isMediaCredentialsModalOpen}
        onClose={() => setIsMediaCredentialsModalOpen(false)}
        mediaCredentialsDescription={event?.media_credentials_description}
        mediaCredentialsFormId={event?.media_credentials_form_id}
        eventId={event?.id}
        eventName={event?.name}
        notificationEmail={event?.media_credentials_notification_email}
        mediaCredentialsPrice={event?.media_credentials_price || 0}
      />

      {/* Coach Attendance Modal - Switch between NCAA Live and Standard */}
      {event?.is_ncaa_live_event ? (
        <NcaaLiveCoachModal
          open={showCoachesModal}
          onOpenChange={setShowCoachesModal}
          eventId={event?.id || ''}
          eventName={event?.name || 'Event'}
          pricing={{
            d1FirstCoachFee: event?.ncaa_d1_first_coach_fee || 0,
            d1AdditionalCoachFee: event?.ncaa_d1_additional_coach_fee || 0,
            otherFirstCoachFee: event?.ncaa_other_first_coach_fee || 0,
            otherAdditionalCoachFee: event?.ncaa_other_additional_coach_fee || 0,
          }}
          hospitalityDays={event?.ncaa_hospitality_days || []}
          instructions={event?.coach_registration_instructions || undefined}
          onSuccess={fetchAttendingCoaches}
        />
      ) : (
        <CoachAttendanceModal
          open={showCoachesModal}
          onOpenChange={setShowCoachesModal}
          eventId={event?.id || ''}
          eventName={event?.name || 'Event'}
          attendanceFee={event?.coach_attendance_fee || 0}
          instructions={event?.coach_registration_instructions || undefined}
          onSuccess={fetchAttendingCoaches}
        />
      )}
    </div>
  );
};

export default EventHelpfulInformation;